import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { pickLocale } from "../lib/format";
import { SmallCaps, BrassRule } from "../components/atelier/primitives";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { ss26Image } from "../components/storefront/assets";
import AdfImage from "../components/storefront/AdfImage";
import SeoMeta from "../components/storefront/SeoMeta";
import { useI18n } from "../lib/i18n";

const JOURNAL_MONTHS = {
  az: ["yanvar", "fevral", "mart", "aprel", "may", "iyun", "iyul", "avqust", "sentyabr", "oktyabr", "noyabr", "dekabr"],
  en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
};

function journalDate(value, locale) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const language = locale === "az" ? "az" : "en";
  return `${date.getDate()} ${JOURNAL_MONTHS[language][date.getMonth()]} ${date.getFullYear()}`;
}

export function JournalIndex() {
  const { locale, t } = useI18n();
  const isAz = locale === "az";
  const [articles, setArticles] = useState([]);
  useEffect(() => { api.get("/journal").then(r => setArticles(r.data || [])).catch(() => {}); }, []);
  return (
    <div className="bg-ivory text-graphite" data-testid="journal-index">
      <SeoMeta
        title={{ az: "Jurnal", en: "The Journal" }}
        description={{
          az: "Parça, mərasim geyimi və düşünülmüş kişi üslubu haqqında atelyedən qeydlər.",
          en: "Long-form notes on cloth, ceremony and the discipline of quiet dressing.",
        }}
        locale={locale}
        canonicalPath="/journal"
      />
      <Header />
      <main id="main">
      <section className="mx-auto max-w-[1440px] px-6 lg:px-12 pt-24 pb-14">
        <SmallCaps>{t("journal.title", "The Journal")}</SmallCaps>
        <h1 className="font-serif text-5xl lg:text-6xl italic mt-4 leading-[1.02] max-w-3xl">{t("journal.subtitle", "Chapters from the atelier.")}</h1>
        <p className="mt-6 max-w-2xl text-graphite/75 font-light leading-relaxed">
          {isAz
            ? "Parça, mərasim geyimi və düşünülmüş kişi üslubu haqqında atelyedən qeydlər."
            : "Long-form notes on cloth, ceremony and the discipline of quiet dressing."}
        </p>
        <BrassRule className="my-14" />
      </section>
      <section className="mx-auto max-w-[1440px] px-6 lg:px-12 pb-32 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20">
        {articles.map((a, idx) => (
          <Link
            key={a.id}
            to={`/journal/${a.slug}`}
            data-testid={`journal-card-${idx}`}
            className="group block"
          >
            <div className="relative overflow-hidden aspect-[16/10] bg-stone/40">
              <AdfImage
                source={a.cover_image_url || ss26Image(idx + 20)}
                alt={pickLocale(a.title, locale)}
                sizes="(min-width: 1024px) 50vw, 100vw"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="mt-6">
              <SmallCaps>{t("journal.chapter", "Məqalə")}</SmallCaps>
              <h2 className="font-serif text-3xl lg:text-4xl mt-3 leading-tight group-hover:text-espresso transition-colors">
                {pickLocale(a.title, locale)}
              </h2>
              {a.published_at && (
                <div className="mt-3 text-[11px] uppercase tracking-widest text-graphite/70">
                  {journalDate(a.published_at, locale)}
                </div>
              )}
            </div>
          </Link>
        ))}
        {articles.length === 0 && (
          <div className="col-span-2 font-serif italic text-graphite/70">{t("journal.empty", "The atelier is preparing new chapters.")}</div>
        )}
      </section>
      </main>
      <Footer />
    </div>
  );
}

export function JournalArticlePage() {
  const { locale, t } = useI18n();
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    api.get(`/journal/${slug}`).then(r => setA(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="bg-ivory min-h-screen text-graphite">
        <Header />
      <main id="main">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12 py-32 text-center">
          <SmallCaps>{t("journal.not_found", "Not found")}</SmallCaps>
          <p className="font-serif italic text-3xl mt-4">{t("journal.missing", "The chapter you sought is not yet written.")}</p>
          <Link to="/journal" className="atelier-link text-[11px] uppercase tracking-widest text-espresso mt-6 inline-block">{t("journal.return", "Return to the journal")}</Link>
        </div>
      </main>
      <Footer />
      </div>
    );
  }
  if (!a) return <div className="bg-ivory min-h-screen"><Header /><main id="main" /></div>;

  const title = pickLocale(a.title, locale);
  const bodyText = pickLocale(a.body || {}, locale);
  const hasRichBody = typeof bodyText === "string" && /<\/?(?:p|h2|h3|ul|ol|li|blockquote|strong|em|u|s|a|br|hr)\b/i.test(bodyText);

  return (
    <div className="bg-ivory text-graphite" data-testid="journal-article">
      <SeoMeta
        title={a.seo_title || a.title}
        description={a.seo_description}
        seo={a.seo || {}}
        locale={locale}
        canonicalPath={`/journal/${a.slug}`}
        image={a.cover_image_url ? mediaUrl(a.cover_image_url) : undefined}
        type="article"
        publishedAt={a.published_at}
        modifiedAt={a.updated_at}
      />
      <Header />
      <main id="main">
      <section className="relative h-[70vh] w-full overflow-hidden">
        <AdfImage source={a.cover_image_url || ss26Image(24)} alt={title} sizes="100vw" priority className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-graphite/70 via-graphite/10 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 pb-20">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-12">
            <SmallCaps tone="ivory" className="text-espresso">{t("journal.chapter", "Məqalə")}</SmallCaps>
            <h1 className="font-serif text-5xl lg:text-6xl italic mt-4 text-ivory leading-[1.02] max-w-3xl">
              {title}
            </h1>
          </div>
        </div>
      </section>
      <article className="mx-auto max-w-2xl px-6 py-24 lg:py-32">
        {a.published_at && (
          <div className="mb-10 text-[11px] uppercase tracking-widest text-graphite/70">
            {journalDate(a.published_at, locale)}
          </div>
        )}
        {hasRichBody ? (
          <div
            className="journal-rich-body font-serif text-lg leading-[1.9] text-graphite/85"
            data-testid="journal-body"
            dangerouslySetInnerHTML={{ __html: bodyText }}
          />
        ) : (
          <div className="font-serif text-lg leading-[1.9] text-graphite/85 whitespace-pre-wrap" data-testid="journal-body">
            {typeof bodyText === "string" ? bodyText : JSON.stringify(bodyText)}
          </div>
        )}
        {!!a.tags?.length && (
          <div className="mt-12 flex flex-wrap gap-2" aria-label={t("journal.tags", "Tag-lər")}>
            {a.tags.map((tag) => (
              <Link key={tag.id} to={`/tags/${tag.slug}`} className="border border-graphite/20 px-3 py-2 text-[11px] uppercase tracking-widest hover:border-espresso hover:text-espresso">
                {tag.name}
              </Link>
            ))}
          </div>
        )}
        <div className="mt-14">
          <Link to="/journal" className="atelier-link text-[11px] uppercase tracking-widest text-espresso">
            ← {t("journal.all_chapters", "Bütün məqalələr")}
          </Link>
        </div>
      </article>
      </main>
      <Footer />
    </div>
  );
}
